/**
 * The two scopes a note can have, and the words the screen uses for them.
 *
 * One module, because three places say the same thing and they must not drift:
 * the composer's choice, the editor's choice, and the marker on every note.
 *
 * **The labels are parallel on purpose.** They used to read "This job" against
 * "The candidate", which sets a job beside a person and leaves the reader to
 * work out that one of them means *every* job. One job against all jobs is the
 * same decision in one dimension. "Only" and "their" say the rest: the narrow
 * one stays put, the wide one follows the candidate.
 *
 * **The legends are verbs**, and that is not decoration either. A reader shown
 * this control above a list of notes read it as a filter over the list — "add"
 * and "move" are things a filter never does, so the control says what it will
 * do rather than what it is about.
 *
 * The values are the server's own vocabulary — a note with an `application_id`
 * is application-scoped, one without is candidate-scoped.
 */

export const THIS_JOB = "application";
export const EVERY_JOB = "candidate";

export const SCOPE_LABEL = {
  [THIS_JOB]: "Only this job",
  [EVERY_JOB]: "All their jobs",
};

/** Above the composer's choice: the note does not exist yet. */
export const LEGEND_NEW = "Add this note to";

/** Above the editor's choice: the note exists and is being refiled. */
export const LEGEND_MOVE = "Move this note to";

/**
 * Which scope a note carries.
 *
 * Read off `applicationId`, never off `jobTitle`: an application whose job row
 * has gone has no title, and keying the scope on the title would show a note
 * held to one job as one that follows the person everywhere — the opposite of
 * what it does.
 *
 * @param {{applicationId?: string|null}} note
 */
export const scopeOf = (note) => (note?.applicationId ? THIS_JOB : EVERY_JOB);

/**
 * The marker beside a note's author and time: a short label, and the sentence
 * behind it.
 *
 * A label rather than a sentence, because it sits in a metadata row and reads
 * as one fact about the note. "Shows on all their jobs" under the body was a
 * sentence nobody asked for, in a place the eye reads as more note.
 *
 * An application-scoped note names its job, so the no-job view — where several
 * applications' notes sit together — is not a wall of remarks with no context.
 * With the job row gone it still marks the note as narrow rather than saying
 * nothing, because saying nothing would read as the other scope.
 *
 * @param {{applicationId?: string|null, jobTitle?: string|null}} note
 * @returns {{label: string, hint: string}}
 */
export const scopeMarker = (note) =>
  scopeOf(note) === THIS_JOB
    ? {
        label: note?.jobTitle || SCOPE_LABEL[THIS_JOB],
        hint: `On ${note?.jobTitle || "this job"} only. It does not appear on this candidate's other jobs.`,
      }
    : {
        label: SCOPE_LABEL[EVERY_JOB],
        hint: "On every job this candidate applies to, now and later.",
      };
